<script setup lang="ts">
// Prime components
import { default as PInputText } from 'primevue/inputtext'
import { default as PSelect } from 'primevue/select'
import { default as PButton } from 'primevue/button'
import { default as PDatePicker } from 'primevue/datepicker'
import { default as PMultipleSelect } from 'primevue/multiselect'
import { default as PMessage } from 'primevue/message'

// Custom imports
import { useGenderOptions, useRoleOptions } from '@/presentation/composables/dataOptions.ts'

// Vue components
import { capitalize, computed, onMounted, reactive, ref } from 'vue'
import { storeToRefs } from 'pinia'
import { useRoute, useRouter } from 'vue-router'
import CBaseHeader from '@/presentation/components/base/CBaseHeader.vue'
import CBaseBlock from '@/presentation/components/base/CBaseBlock.vue'
import { useUsersStore } from '@/presentation/stores/users/users.ts'
import { z } from 'zod'
import { UserGender } from '@/core/domain/resources/enums/UserGender.ts'
import { UserRole } from '@/core/domain/resources/enums/UserRole.ts'
import {
  UserBirthdateSchema
} from '@/core/infrastructure/schemas/utils/UserBirthdateSchema.ts'
import { UserIdentifierSchema } from '@/core/infrastructure/schemas/utils/UserIdentifierSchema.ts'
import { UserListRolesSchema } from '@/core/infrastructure/schemas/utils/UserListRolesSchema.ts'
import { UserGenderSchema } from '@/core/infrastructure/schemas/utils/UserGenderSchema.ts'
import { UserEmailSchema } from '@/core/infrastructure/schemas/utils/UserEmailSchema.ts'
import { UserInput } from '@/core/domain/users/UserInput.ts'

// Types
const UserFormDraftSchema = z.object({
  identifier: UserIdentifierSchema.optional(),
  firstname: z
    .string()
    .trim()
    .min(1, 'Le prénom est requis')
    .max(150, 'Le prénom est trop long')
    .transform((value) => capitalize(value))
    .optional(),
  lastname: z
    .string()
    .trim()
    .toUpperCase()
    .min(1, 'Le nom est requis')
    .max(150, 'Le nom est trop long')
    .optional(),
  email: UserEmailSchema.optional(),
  gender: UserGenderSchema,
  roles: UserListRolesSchema,
  birthdate: UserBirthdateSchema.optional()
})
type UserFormDraft = z.infer<typeof UserFormDraftSchema>

const useRoleOptionsWithDisabled = computed(() =>
  useRoleOptions.value.map((role) => ({
    ...role,
    disabled: !Object.values(UserRole.BackOfficeValues)
      .map((it) => it as string)
      .includes(role.code)
  }))
)

// Composables
const usersStore = useUsersStore()

// Stores refs
const { usersStoreLoading, usersStoreError } = storeToRefs(usersStore)
const route = useRoute()
const router = useRouter()

// Refs
const form = reactive<UserFormDraft>({
  firstname: '',
  lastname: '',
  email: '',
  gender: UserGender.Values.UNSPECIFIED,
  roles: [],
  birthdate: null
})
const errors = ref<Record<string, string>>({})

const loading = computed(
  () =>
    usersStoreLoading.value.fetchByIdentifier ||
    usersStoreLoading.value.createUser ||
    usersStoreLoading.value.updateUser
)

async function onSubmit(input: UserFormDraft) {
  errors.value = {}
  const parsed = UserFormDraftSchema.safeParse(input)

  if (parsed.success) {
    // Update user
    if (route.name === 'edit-user') {
      await usersStore.updateUser(UserInput.create(parsed.data))
    }
    // Create user
    else {
      await usersStore.createUser(UserInput.create(parsed.data))
    }

    if (!usersStoreError.value.hasError) await router.push({ name: 'users-list' })
  } else {
    const { fieldErrors } = parsed.error.flatten()
    const mapped: Partial<Record<keyof UserFormDraft, any>> = {}
    for (const k of Object.keys(fieldErrors) as (keyof typeof fieldErrors)[]) {
      const msg = fieldErrors[k]?.[0]
      if (msg) mapped[k as keyof UserFormDraft] = msg
    }
    errors.value = mapped
  }
}

onMounted(async () => {
  // Check if this creation or edition
  if (route.name === 'edit-user') {
    // Get user id from route params
    const identifier = route.params.identifier as string
    // Get user from store
    await usersStore.fetchByIdentifier(identifier)
    const u = usersStore.getUserByIdentifier(identifier)
    if (usersStoreError.value.hasError) {
      // Redirect to users list
      usersStore.showErrorToast('Une erreur est survenue', usersStoreError.value.message)
      await router.push({ name: 'users-list' })
      return
    }
    Object.assign(form, u)
  }
})
</script>

<template>
  <c-base-header title="Gestion des utilisateurs" :display-breadcrumbs="false" />

  <div class="col-span-12 flex-1 gap-8 overflow-y-auto p-6 pt-2 md:p-8">
    <c-base-block title="Ajouter un utilisateur">
      <p>Les champs marqués d'un * sont obligatoires.</p>

      <form class="mt-12 grid grid-cols-12 gap-4" @submit.prevent="onSubmit(form)" novalidate>
        <!--      Prénom   -->
        <div class="col-span-12 flex flex-col md:col-span-6">
          <label for="firstname" class="h5 mb-2"> Prénom* </label>
          <p-input-text
            id="firstname"
            type="text"
            required
            v-model="form.firstname"
            autocomplete="off"
            data-form-type="other"
          />
          <p-message
            v-if="errors['firstname']"
            size="small"
            class="text-red-500"
            severity="error"
            variant="simple"
            >{{ errors['firstname'] }}</p-message
          >
        </div>

        <!--      Nom   -->
        <div class="col-span-12 flex flex-col md:col-span-6">
          <label for="lastname" class="h5 mb-2"> Nom* </label>
          <p-input-text
            id="lastname"
            type="text"
            required
            v-model="form.lastname"
            autocomplete="off"
            data-form-type="other"
          />
          <p-message
            v-if="errors['lastname']"
            size="small"
            class="text-red-500"
            severity="error"
            variant="simple"
            >{{ errors['lastname'] }}</p-message
          >
        </div>

        <!--      Email   -->
        <div class="col-span-12 flex flex-col md:col-span-6">
          <label for="email" class="h5 mb-2"> Email* </label>
          <p-input-text
            id="email"
            type="email"
            required
            v-model="form.email"
            autocomplete="off"
            data-form-type="other"
          />
          <p-message
            v-if="errors['email']"
            size="small"
            class="text-red-500"
            severity="error"
            variant="simple"
            >{{ errors['email'] }}</p-message
          >
        </div>

        <!--      Genre   -->
        <div class="col-span-12 flex flex-col md:col-span-6">
          <label for="gender" class="h5 mb-2"> Genre* </label>
          <p-select
            id="gender"
            v-model="form.gender"
            :options="useGenderOptions"
            optionLabel="name"
            optionValue="code"
            aria-required="true"
          />
          <p-message
            v-if="errors['gender']"
            size="small"
            class="text-red-500"
            severity="error"
            variant="simple"
            >{{ errors['gender'] }}</p-message
          >
        </div>

        <!--      Date de naissance   -->
        <div class="col-span-12 flex flex-col md:col-span-6">
          <label for="birthdate" class="h5 mb-2"> Date de naissance </label>
          <p-date-picker
            id="birthdate"
            v-model="form.birthdate"
            :showIcon="true"
            :showButtonBar="true"
            locale="fr"
            dateFormat="dd/mm/yy"
            panel-class="w-40"
          />
          <p-message
            v-if="errors['birthdate']"
            size="small"
            class="text-red-500"
            severity="error"
            variant="simple"
            >{{ errors['birthdate'] }}</p-message
          >
        </div>

        <!--     Rôle   -->
        <div class="col-span-12 flex flex-col md:col-span-6">
          <label for="roles" class="h5 mb-2"> Rôle* </label>
          <p-multiple-select
            id="roles"
            v-model="form.roles"
            :showToggleAll="false"
            :options="useRoleOptionsWithDisabled"
            optionLabel="name"
            optionValue="code"
            optionDisabled="disabled"
          />
          <p-message
            v-if="errors['roles']"
            size="small"
            class="text-red-500"
            severity="error"
            variant="simple"
            >{{ errors['roles'] }}</p-message
          >
        </div>

        <div class="col-span-12 mt-12 grid grid-cols-2 gap-4 md:justify-end md:gap-8">
          <p-button
            class="col-span-12 md:col-span-1 md:justify-self-start"
            label="Annuler"
            type="button"
            severity="secondary"
            @click.prevent="$router.push({ name: 'users-list' })"
            :loading="loading"
          />
          <p-button
            class="col-span-12 md:col-span-1 md:justify-self-end"
            label="Valider"
            type="submit"
            severity="primary"
            :loading="loading"
          />
        </div>
      </form>
    </c-base-block>
  </div>
</template>
